import { FC, useCallback, useState } from 'react';

import { MultiSelectDisplay } from '#/client/models';
import { FullPageModal } from '#/components/Modal/FullPageModal';
import { useConfig } from '#/hooks/useConfig';
import { ButtonVariant } from '#/service/constants';
import { modalActions } from '#/store/modal/modal.store';
import { tb } from '#/store/translation/translation.store';

import {
  Button,
  ButtonContainer,
  Card,
  CardCheckmarkIcon,
  CardContainer,
  CardImage,
  CardImageContainer,
  CardInput,
  CardText,
  Container,
  Description,
  Title,
} from './PreferencesModal.styled';

interface Props {
  multiSelectInput: MultiSelectDisplay;
  selectedValues: string[];
  isUpdating: boolean;
  onUpdate: (selectedValues: string[], showSnackbar: boolean) => Promise<void>;
  onClose: () => void;
}

export const PreferencesModal = ({
  multiSelectInput,
  selectedValues,
  isUpdating = false,
  onUpdate,
  onClose,
}: Props) => {
  const {
    app: { staticFilePrefix },
  } = useConfig();

  const { label, description, values = [] } = multiSelectInput;

  const [selected, setSelected] = useState(() => new Set(selectedValues));

  const isUserChangingPreference = Boolean(
    selected.size || selectedValues?.length
  );

  const handleSelect = useCallback((optionKey: string) => {
    setSelected((prevState) => {
      if (!prevState.delete(optionKey)) {
        prevState.add(optionKey);
      }

      return new Set(prevState);
    });
  }, []);

  const handleButtonClick = useCallback(async () => {
    try {
      await onUpdate?.([...selected], isUserChangingPreference);
      onClose();
    } catch {
      modalActions.showModal({
        title: tb('error.generic.title'),
        message: tb('error.generic.message'),
        buttonLabel: tb('button.ok'),
      });
    }
  }, [selected, onClose, onUpdate, isUserChangingPreference]);

  return (
    <FullPageModal closeOnEsc={false}>
      <Container>
        <Title>{tb(label)}</Title>
        <Description>{tb(description)}</Description>
        <CardContainer>
          {values.map(({ key, label, imageUrl, value }) => (
            <PreferenceCard
              isSelected={selected.has(key)}
              key={key}
              label={tb(label)}
              onSelect={handleSelect}
              optionKey={key}
              src={imageUrl ?? `${staticFilePrefix}images/missing-image.svg`}
              value={tb(value)}
            />
          ))}
        </CardContainer>
        <ButtonContainer>
          <Button
            isLoading={isUpdating}
            onClick={handleButtonClick}
            variant={ButtonVariant.Primary}
          >
            {isUserChangingPreference ? tb('button.ok') : tb('button.cancel')}
          </Button>
        </ButtonContainer>
      </Container>
    </FullPageModal>
  );
};

const PreferenceCard: FC<{
  isSelected: boolean;
  optionKey: string;
  onSelect: (optionKey: string) => void;
  label: string;
  src: string;
  value: string;
}> = ({ isSelected, optionKey, onSelect, label, src, value }) => {
  const handleChange = useCallback(() => {
    onSelect(optionKey);
  }, [optionKey, onSelect]);

  return (
    <Card>
      <CardInput
        aria-checked={isSelected}
        checked={isSelected}
        onChange={handleChange}
        type="checkbox"
      />
      <CardImageContainer>
        <CardImage $selected={isSelected} alt={label} src={src} />
        <CardCheckmarkIcon $selected={isSelected} name="checkmark" />
      </CardImageContainer>
      <CardText>{value}</CardText>
    </Card>
  );
};
